import { Scene, ArcRotateCamera, Vector3, Color4, CubeTexture } from '@babylonjs/core';
import { stringUtil } from '@/models/stringUtil';
import store from '@/store';
import { ProductType, Product } from '@/store/modules/product';
import { Triple } from './triple';
import Element, { ElementType } from './element';
import { FurnitureManager } from './furnitureManager';

export class SceneConfiguration {

    public useZoomOnModelOnly = true;
    public pointerOnModel = false;
    private furnitureManger = new FurnitureManager();

    private cameraLimits: Array<Triple<ProductType, number, number>> = [
        new Triple(ProductType.CHEST_FURNITURE, Math.PI * 70 / 100, Math.PI / 10),
        new Triple(ProductType.BEDSIDE_TABLE, Math.PI * 53 / 100, Math.PI / 10),
        new Triple(ProductType.DESK, Math.PI * 53 / 100, Math.PI / 10),
        new Triple(ProductType.TABLE, Math.PI * 43 / 100, Math.PI / 10),
        new Triple(ProductType.BED, Math.PI * 47 / 100, Math.PI / 10),
        new Triple(ProductType.COFFEE_TABLE, Math.PI * 60 / 100, Math.PI / 10),
        new Triple(ProductType.DRESSING, Math.PI * 53 / 100, Math.PI / 10),
    ];

    constructor(private scene: Scene, private canvas: any) { }

    public run() {
        this.createEnvironment();
        this.createCamera();
        this.subscribeStore();
    }

    public centerCamera() {
        const product = store.getters.product;
        this.camera.position = this.getCenterVector(product);
        this.camera.radius = this.getRadius();
    }

    public setTarget(element: Element): Vector3 {
        const rootPosition = element.rootPosition;
        rootPosition.x *= -1;
        rootPosition.z *= -1;
        const product: Product = store.getters.product;
        if (product.haveTop) {
            const legs = this.furnitureManger.legs[0];
            if (legs) {
                rootPosition.y = (rootPosition.y + legs.rootPosition.y) / 2;
            }
        }
        this.camera.target = rootPosition;
        return rootPosition;
    }

    private get camera(): ArcRotateCamera {
        return this.scene.cameras[0] as ArcRotateCamera;
    }

    private getRadius(): number {
        const body: Element = store.getters.body;
        if (body) {
            const legs = store.getters.scene.elements.find((x) =>
                x.type === ElementType.LEGS &&
                x.shaderName !== 'ShaderNPodp',
            );
            if (body.shape.x === 1 && body.shape.y === 1 && !legs) {
                return 150;
            }

            const width = body.width / 2;
            const height = body.height / 2 + (legs ? legs.height : 0);
            const depth = body.depth / 2;
            const radius = Math.max(width, height, depth);
            const aspectRatio = this.scene.getEngine().getAspectRatio(this.camera);
            let halfMinFov = this.camera.fov / 2.45;
            if (aspectRatio < 1) {
                halfMinFov = Math.atan(aspectRatio * Math.tan(halfMinFov));
            }
            const viewRadius = Math.abs(radius / Math.sin(halfMinFov));
            return viewRadius;
        }
        return this.camera.radius;
    }

    private createEnvironment() {
        this.scene.clearColor = new Color4(1, 1, 1, 1);
        const hdrTexture = CubeTexture.CreateFromPrefilteredData(
            stringUtil.createDownloadUrl('enviro/environment.env'),
            this.scene,
        );
        hdrTexture.gammaSpace = true;
        this.scene.environmentTexture = hdrTexture;
        store.commit('setHdrTexture', hdrTexture);
    }

    private createCamera() {
        const camera = new ArcRotateCamera('Camera', 0, 0, 0,
            new Vector3(0, 0, 10), this.scene);

        this.centerCamera();
        camera.lowerRadiusLimit = 100;
        camera.upperRadiusLimit = 1000;
        camera.fov = 0.523;
        camera.attachControl(this.canvas);

        if (this.useZoomOnModelOnly) {

            // https://www.html5gamedevs.com/topic/4421-keep-mouse-wheel-scrolling-html-page-instead-of-zooming-arcrotatecamera/

            (window as any).isPointerOnModel = () => {
                return this.pointerOnModel;
            };

            this.canvas.addEventListener('pointermove', () => {
                if (this.scene) {
                    const pickInfo = this.scene.pick(this.scene.pointerX, this.scene.pointerY, null, false, this.camera);
                    this.pointerOnModel = pickInfo !== undefined && pickInfo.hit;
                }
            }, false);
        }

        this.scene.activeCameras.push(camera);
    }

    private subscribeStore() {
        store.subscribe((mutation, state) => {
            if (mutation.type === 'setProduct') {
                this.setCameraLimits();
            }
        });
    }

    private setCameraLimits() {
        const type: ProductType = store.getters.product.type;
        const limit = this.cameraLimits.find((x) => x.x === type)!;
        if (!limit) { return; }
        this.camera.upperBetaLimit = limit.y;
        this.camera.lowerBetaLimit = limit.z;
    }

    private getCenterVector(project: any) {
        const body: Element = store.getters.body;
        if (body) {
            const rootPosition = this.setTarget(body);
            const x = rootPosition.x - body.width * 1.1;
            const y = rootPosition.y;
            const z = -2 * (body.width + body.height + body.depth);
            return new Vector3(x, y, z);
        } else if (project.cameraPosition) {
            return new Vector3(project.cameraPosition.x, project.cameraPosition.y, project.cameraPosition.z);
        } else {
            return new Vector3(20, 100, -300);
        }
    }
}
